import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { DispensingView, PharmacyItemView, StoreView, WorkLineView } from '@wodi/shared';
import {
  AlertPanel,
  Button,
  Dialog,
  DialogActions,
  EmptyState,
  LoadingBlock,
  Notice,
  Select,
  Tag,
  TextArea,
  TextField,
  useShortcut,
} from '@wodi/ui';
import { useState } from 'react';

import {
  closeBalance,
  createDispense,
  dispensePrepared,
  dispenseQuery,
  dispensingKeys,
  dispensingQuery,
  pharmacyQueueQuery,
  recordCounselling,
} from '../api/dispensing.ts';
import { can, sessionQuery } from '../api/session.ts';
import { money } from '../billing/format.ts';
import { useOnline } from '../hooks/use-online.ts';
import { outsideDialogs, screen, ShortcutHint } from '../layout/screen.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant, formatIsoDate, formatTimeOnly } from '../patients/format.ts';
import { WithPharmacyStore } from '../pharmacy/common.tsx';
import { PharmacyItemPicker } from '../pharmacy/item-picker.tsx';
import { DispenseLabels } from '../pharmacy/labels.tsx';
import { ShaApprovalTag } from '../sha/approval-tag.tsx';
import styles from '../pharmacy/pharmacy.module.css';
import { errorText, newRequestKey } from '../stock/common.tsx';
import { formatQuantity, readQuantity } from '../stock/format.ts';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/pharmacy');

const QUEUE_WORDS = {
  new: 'New',
  being_prepared: 'Being prepared',
  waiting_for_approval: 'Waiting for SHA approval',
  waiting_for_payment: 'Waiting for payment',
  ready_to_dispense: 'Ready to dispense',
  part_dispensed: 'Part dispensed',
} as const;

const LINE_WORDS: Record<WorkLineView['state'], string> = {
  new: 'Not given yet',
  prepared: 'Prepared',
  part_dispensed: 'Part given',
  dispensed: 'Given',
  closed: 'Balance closed',
  stopped: 'Stopped by the prescriber',
};

/** The prescribing slice's allergy rules, in the pharmacist's words (ids from prescribing). */
const ALLERGY_WORDS: Record<string, string> = {
  'rx.allergy.match': 'A medicine matches a recorded allergy.',
  'rx.allergy.uncoded':
    'Some recorded allergies are not coded, so Wodi could not check them against the medicines.',
  'rx.allergy.not_matchable':
    'A medicine has no ingredients in the formulary, so Wodi could not check it against the allergies.',
  'rx.allergy.not_recorded': 'Allergies are not recorded for this patient, so none were checked.',
};

const CLOSE_REASONS = [
  { id: 'patient_declined', label: 'Patient declined' },
  { id: 'bought_elsewhere', label: 'Bought elsewhere' },
  { id: 'out_of_stock', label: 'Out of stock' },
  { id: 'prescriber_changed', label: 'Prescriber changed it' },
  { id: 'other', label: 'Other' },
] as const;

/**
 * The prescription queue and dispensing (DESIGN.md §8 Pharmacy; plan §8.1): the queue on
 * the left, the chosen prescription on the right with each line's batch (earliest expiry),
 * stock after and price. "Dispense and print labels" (Ctrl/⌘Enter) is the primary action.
 * The address carries the prescription's UUID only.
 */
export function PharmacyDispensingPage() {
  const { prescriptionId } = route.useSearch();
  return (
    <WithPharmacyStore title="Prescriptions">
      {(store) => (
        <div className={screen.split}>
          <Queue selected={prescriptionId ?? null} />
          {prescriptionId === undefined ? (
            <EmptyState>Choose a prescription on the left. Press N for the oldest.</EmptyState>
          ) : (
            <WorkView
              key={`${prescriptionId}-${store.id}`}
              prescriptionId={prescriptionId}
              store={store}
            />
          )}
        </div>
      )}
    </WithPharmacyStore>
  );
}

function Queue({ selected }: { selected: string | null }) {
  const queue = useQuery({ ...pharmacyQueueQuery, refetchInterval: 15_000 });
  const navigate = route.useNavigate();
  const oldest = queue.data?.items[0];
  useShortcut(
    'n',
    outsideDialogs(() => {
      if (oldest !== undefined)
        void navigate({ search: { prescriptionId: oldest.prescriptionId } });
    }),
    { enabled: oldest !== undefined, description: 'Open the oldest prescription' },
  );
  const count = queue.data?.items.length ?? 0;
  return (
    <nav aria-label="Prescriptions waiting" className={`${styles.section} ${screen.panel}`}>
      <div className={styles.queueHeader}>
        <h2 className={styles.sectionHeading}>Waiting</h2>
        {count > 0 ? (
          <span className={styles.meta}>
            {count === 1 ? '1 prescription' : `${String(count)} prescriptions`}
          </span>
        ) : null}
      </div>
      {queue.isPending ? <LoadingBlock label="Loading prescriptions" rows={4} /> : null}
      {queue.isError ? (
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void queue.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(queue.error, "Prescriptions couldn't be loaded. Try again.")}
        </Notice>
      ) : null}
      {queue.isSuccess && !queue.data.connected ? (
        <EmptyState>
          Prescriptions from consultations appear here once prescribing is switched on. Walk-in
          sales use Counter sale.
        </EmptyState>
      ) : null}
      {queue.isSuccess && queue.data.connected && queue.data.items.length === 0 ? (
        <EmptyState>
          No prescriptions waiting. Prescriptions sent from consultation appear here.
        </EmptyState>
      ) : null}
      {queue.isSuccess && queue.data.items.length > 0 ? (
        <div className={styles.queue}>
          {queue.data.items.map((item) => (
            <Link
              key={item.prescriptionId}
              to="/pharmacy"
              search={{ prescriptionId: item.prescriptionId }}
              className={styles.queueItem}
              {...(selected === item.prescriptionId ? { 'aria-current': 'page' as const } : {})}
            >
              <span className={styles.queueLine}>
                <strong>{item.patient.displayName}</strong>
                <span>{`Since ${formatTimeOnly(item.signedAt)}`}</span>
              </span>
              <span className={`${styles.queueLine} ${styles.meta}`}>
                <span className="wodi-id">{item.patient.patientNumber}</span>
                <Tag
                  state={
                    item.state === 'waiting_for_payment' || item.state === 'waiting_for_approval'
                      ? 'attention'
                      : 'active'
                  }
                >
                  {QUEUE_WORDS[item.state]}
                </Tag>
              </span>
            </Link>
          ))}
        </div>
      ) : null}
      {oldest === undefined ? null : <ShortcutHint combo="n" action="open the oldest" />}
    </nav>
  );
}

interface Draft {
  giveText: string;
  instructions: string;
  note: string;
  reference: string;
  substitute: PharmacyItemView | null;
  substitutionReason: string;
}

function WorkView({ prescriptionId, store }: { prescriptionId: string; store: StoreView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const online = useOnline();
  const queryClient = useQueryClient();
  const view = useQuery({
    ...dispensingQuery(prescriptionId, store.id),
    // While a line waits for payment or SHA, poll so it turns ready without a refresh.
    refetchInterval: (q) =>
      q.state.data?.lines.some((l) =>
        l.lines.some(
          (x) =>
            x.gate?.state === 'waiting_for_payment' || x.gate?.state === 'waiting_for_approval',
        ),
      )
        ? 5_000
        : false,
  });
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [ackReason, setAckReason] = useState('');
  const [requestKey, setRequestKey] = useState(newRequestKey);
  const [dispensedId, setDispensedId] = useState<string | null>(null);
  const [closing, setClosing] = useState<WorkLineView | null>(null);
  const [substituting, setSubstituting] = useState<WorkLineView | null>(null);

  const draftOf = (line: WorkLineView): Draft =>
    drafts[line.id] ?? {
      giveText: line.suggested > 0 ? String(line.suggested) : '',
      instructions: line.instructions ?? '',
      note: '',
      reference: '',
      substitute: null,
      substitutionReason: '',
    };
  const setDraft = (line: WorkLineView, change: Partial<Draft>) => {
    setDrafts((all) => ({ ...all, [line.id]: { ...draftOf(line), ...change } }));
  };

  const data = view.data;
  const interruptive = data?.allergyFindings.filter((f) => f.tier === 'interruptive') ?? [];
  const blocking = data?.allergyFindings.filter((f) => f.tier === 'blocking') ?? [];
  const toGive = (data?.lines ?? []).filter(
    (l) => l.status === 'active' && (readQuantity(draftOf(l).giveText) ?? 0) > 0,
  );

  const submit = useMutation({
    mutationFn: (d: DispensingView) =>
      createDispense({
        clientRequestId: requestKey,
        prescriptionId: d.prescriptionId,
        storeId: store.id,
        dispenseNow: true,
        lines: toGive.map((l) => {
          const draft = draftOf(l);
          return {
            prescriptionLineId: l.id,
            prescriptionLineVersion: l.version,
            serviceItemId: draft.substitute?.id ?? l.serviceItemId,
            quantity: readQuantity(draft.giveText) ?? 0,
            ...(draft.substitute === null
              ? {}
              : { substitutionReason: draft.substitutionReason.trim() }),
            ...(draft.instructions.trim() !== '' && draft.instructions.trim() !== l.instructions
              ? { instructions: draft.instructions.trim() }
              : {}),
            ...(draft.note.trim() === '' ? {} : { pharmacistNote: draft.note.trim() }),
            ...(draft.reference.trim() === ''
              ? {}
              : { controlledRegisterReference: draft.reference.trim() }),
          };
        }),
        acknowledgements: interruptive.map((f) => ({ ruleId: f.ruleId, reason: ackReason.trim() })),
      }),
    onSuccess: async (d) => {
      await queryClient.invalidateQueries({ queryKey: dispensingKeys.all });
      const given = d.lines.filter((l) => l.status === 'dispensed').length;
      toasts.show({
        title: given > 0 ? 'Dispensed. Labels sent to the printer' : 'Prepared',
        description: d.dispenseNumber,
      });
      setDispensedId(given > 0 ? d.id : null);
      setDrafts({});
      setRequestKey(newRequestKey());
    },
  });

  const go = () => {
    if (data !== undefined && toGive.length > 0 && online && !submit.isPending) submit.mutate(data);
  };
  useShortcut('mod+enter', go, {
    allowInFields: true,
    description: 'Dispense and print labels',
  });

  if (view.isPending) return <LoadingBlock label="Loading the prescription" rows={4} />;
  if (view.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void view.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(view.error, "The prescription couldn't be loaded. Try again.")}
      </Notice>
    );
  const d = view.data;
  const mayDispense = session !== null && can(session, 'pharmacy.dispense');
  const maySubstitute = session !== null && can(session, 'pharmacy.substitute');
  return (
    <section className={`${styles.section} ${screen.panel}`} aria-label="Prescription">
      <PatientBannerLoader patientId={d.patientId} />
      <p
        className={styles.meta}
      >{`Prescribed by ${d.prescriberDisplay}, ${formatInstant(d.signedAt)}`}</p>
      {blocking.length > 0 ? (
        <Notice tone="error">
          A recorded allergy blocks a medicine on this prescription. Ask the prescriber for another;
          it can't be dispensed.
        </Notice>
      ) : null}
      {interruptive.length > 0 ? (
        <AlertPanel
          danger={interruptive.some((f) => f.ruleId === 'rx.allergy.match')}
          title="Check these medicines against the allergies in the banner."
          reason={ackReason}
          onReasonChange={setAckReason}
          reasonLabel="What you checked"
        >
          <ul>
            {interruptive.map((f) => (
              <li key={f.ruleId}>
                {ALLERGY_WORDS[f.ruleId] ?? 'An allergy check needs your answer.'}
              </li>
            ))}
          </ul>
          <p>Drug interactions are not checked.</p>
        </AlertPanel>
      ) : null}
      {d.lines.map((line) => {
        const draft = draftOf(line);
        const give = readQuantity(draft.giveText);
        return (
          <fieldset key={line.id} className={styles.line}>
            <legend className={styles.lineHeading}>
              <span>{line.medicationDisplay}</span>
              <Tag state={line.remaining > 0 ? 'pending' : 'done'}>{LINE_WORDS[line.state]}</Tag>
              <ShaApprovalTag approval={line.shaApproval} />
            </legend>
            <dl className={styles.facts}>
              <dt>Instructions</dt>
              <dd>{line.instructions ?? line.instructionsProblem ?? ''}</dd>
              <dt>Prescribed</dt>
              <dd className={styles.numbers}>
                {formatQuantity(line.prescribed, line.baseUnitDisplay)}
              </dd>
              <dt>Given</dt>
              <dd className={styles.numbers}>
                {formatQuantity(line.dispensed, line.baseUnitDisplay)}
              </dd>
              <dt>Still owed</dt>
              <dd className={styles.numbers}>
                {formatQuantity(line.remaining, line.baseUnitDisplay)}
              </dd>
              <dt>In stock</dt>
              <dd className={styles.numbers}>
                {formatQuantity(line.usable, line.baseUnitDisplay)}
                {line.preparedForOthers > 0
                  ? `; prepared for others ${formatQuantity(line.preparedForOthers, line.baseUnitDisplay)}`
                  : ''}
                {give !== null
                  ? `; after this ${formatQuantity(Math.max(0, line.usable - give), line.baseUnitDisplay)}`
                  : ''}
              </dd>
              <dt>Price</dt>
              <dd>
                {line.unitPrice === null
                  ? `Not priced: ${line.unpricedReason ?? ''}`
                  : `${money(line.unitPrice)} each`}
              </dd>
              {line.batches.length > 0 ? (
                <>
                  <dt>Batch</dt>
                  <dd>
                    {line.batches.map((b, i) => (
                      <span key={b.batchId}>
                        {i > 0 ? '; ' : null}
                        <span className="wodi-id">{b.batchNumber}</span>
                        {`${b.expiryDate === null ? '' : `, expires ${formatIsoDate(b.expiryDate)}`}${i === 0 ? ' (earliest expiry)' : ''}`}
                      </span>
                    ))}
                  </dd>
                </>
              ) : null}
            </dl>
            {line.warning === 'out_of_stock' ? (
              <Notice tone="info">None in stock here. Close the balance, or give it later.</Notice>
            ) : null}
            {line.warning === 'short' ? (
              <Notice tone="info">{`Only ${String(line.usable)} in stock: ${String(line.shortBy)} will still be owed.`}</Notice>
            ) : null}
            {line.warning === 'prepared_for_others' ? (
              <Notice tone="info">
                Some of this stock is already prepared for other patients.
              </Notice>
            ) : null}
            {line.lines.map((p) => (
              <p key={p.id} className={styles.meta}>
                <span className="wodi-id">{p.dispenseNumber}</span>
                {`: ${formatQuantity(p.quantity, line.baseUnitDisplay)} ${p.status === 'dispensed' ? 'given' : p.status}`}
                {p.gate?.state === 'waiting_for_payment'
                  ? `. Waiting for payment: ${money(p.gate.owed)}`
                  : ''}
                {p.gate?.state === 'waiting_for_approval' ? '. Waiting for SHA approval' : ''}
                {p.gate?.state === 'approval_declined'
                  ? `. SHA declined${p.gate.reason === null ? '' : `: ${p.gate.reason}`}`
                  : ''}
                {p.gate?.state === 'open' ? '. Ready to dispense' : ''}
                {p.substituted ? `. Substituted: ${p.itemName}` : ''}
              </p>
            ))}
            {mayDispense && line.remaining > 0 && line.status === 'active' ? (
              <div className={styles.row}>
                <div className={styles.quantity}>
                  <TextField
                    label="Give"
                    unit={line.baseUnitDisplay}
                    inputMode="numeric"
                    value={draft.giveText}
                    onChange={(v) => {
                      setDraft(line, { giveText: v });
                    }}
                    {...(give !== null && give > line.remaining
                      ? { errorMessage: `At most ${String(line.remaining)}.` }
                      : {})}
                  />
                </div>
                {line.instructions === null ? (
                  <TextField
                    label="Instructions for the label"
                    description={line.instructionsProblem ?? ''}
                    value={draft.instructions}
                    onChange={(v) => {
                      setDraft(line, { instructions: v });
                    }}
                  />
                ) : null}
                <TextField
                  label="Extra label line (optional)"
                  value={draft.note}
                  onChange={(v) => {
                    setDraft(line, { note: v });
                  }}
                />
                {line.isControlled ? (
                  <TextField
                    label="Register page and entry"
                    description="Record it in the controlled drugs register book first."
                    value={draft.reference}
                    onChange={(v) => {
                      setDraft(line, { reference: v });
                    }}
                  />
                ) : null}
              </div>
            ) : null}
            {draft.substitute === null ? null : (
              <p>{`Giving ${draft.substitute.name} instead. ${draft.substitutionReason}`}</p>
            )}
            {mayDispense && line.remaining > 0 && line.status === 'active' ? (
              <div className={styles.actions}>
                {maySubstitute ? (
                  <Button
                    variant="quiet"
                    onPress={() => {
                      setSubstituting(line);
                    }}
                    isDisabled={!line.substitutionAllowed}
                  >
                    {line.substitutionAllowed ? 'Substitute' : 'No substitution (prescriber)'}
                  </Button>
                ) : null}
                <Button
                  variant="quiet"
                  onPress={() => {
                    setClosing(line);
                  }}
                >
                  Close balance
                </Button>
              </div>
            ) : null}
          </fieldset>
        );
      })}
      <ReadyToDispense view={d} />
      {submit.isError ? (
        <Notice tone="error">{errorText(submit.error, 'Nothing was dispensed. Try again.')}</Notice>
      ) : null}
      {online ? null : (
        <Notice tone="info">
          Dispensing needs a connection to Wodi. Give medicines against the paper prescription and
          record them here when Wodi is back.
        </Notice>
      )}
      {mayDispense ? (
        <div className={styles.actions}>
          <Button
            variant="primary"
            onPress={go}
            isPending={submit.isPending}
            pendingLabel="Dispensing"
            isDisabled={!online || toGive.length === 0}
            shortcut="mod+enter"
          >
            Dispense and print labels
          </Button>
          {toGive.length > 0 ? (
            <ShortcutHint combo="mod+enter" action="dispense and print labels" />
          ) : null}
        </div>
      ) : null}
      {dispensedId === null ? null : (
        <>
          <DispenseLabels dispenseId={dispensedId} auto />
          <Counselling dispenseId={dispensedId} />
        </>
      )}
      {closing === null ? null : (
        <CloseBalanceDialog
          line={closing}
          onClose={() => {
            setClosing(null);
          }}
        />
      )}
      {substituting === null ? null : (
        <SubstituteDialog
          storeId={store.id}
          onClose={() => {
            setSubstituting(null);
          }}
          onChoose={(item, reason) => {
            setDraft(substituting, { substitute: item, substitutionReason: reason });
            setSubstituting(null);
          }}
        />
      )}
    </section>
  );
}

/** Prepared lines whose payment has come in: one action hands them over. */
function ReadyToDispense({ view }: { view: DispensingView }) {
  const queryClient = useQueryClient();
  const ready = view.lines.flatMap((l) =>
    l.lines.filter((p) => p.status === 'prepared' && p.gate?.state === 'open'),
  );
  const byDispense = new Map<string, string[]>();
  for (const r of ready)
    byDispense.set(r.dispenseId, [...(byDispense.get(r.dispenseId) ?? []), r.id]);
  const [done, setDone] = useState<string | null>(null);
  const hand = useMutation({
    mutationFn: async () => {
      let last: string | null = null;
      for (const [dispenseId, lineIds] of byDispense) {
        const current = await queryClient.query({
          ...dispenseQuery(dispenseId),
          staleTime: 0,
        });
        await dispensePrepared(dispenseId, { expectedVersion: current.version, lineIds });
        last = dispenseId;
      }
      return last;
    },
    onSuccess: async (last) => {
      await queryClient.invalidateQueries({ queryKey: dispensingKeys.all });
      toasts.show({ title: 'Dispensed. Labels sent to the printer' });
      setDone(last);
    },
  });
  if (ready.length === 0 && done === null) return null;
  return (
    <div className={styles.section}>
      {ready.length > 0 ? (
        <div className={styles.actions}>
          <span>{`${String(ready.length)} prepared ${ready.length === 1 ? 'medicine is' : 'medicines are'} paid for and ready.`}</span>
          <Button
            onPress={() => {
              hand.mutate();
            }}
            isPending={hand.isPending}
            pendingLabel="Dispensing"
          >
            Dispense prepared
          </Button>
        </div>
      ) : null}
      {hand.isError ? (
        <Notice tone="error">{errorText(hand.error, 'Nothing was dispensed.')}</Notice>
      ) : null}
      {done === null ? null : <DispenseLabels dispenseId={done} auto />}
    </div>
  );
}

function Counselling({ dispenseId }: { dispenseId: string }) {
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => recordCounselling(dispenseId, note.trim()),
    onSuccess: () => {
      toasts.show({ title: 'Counselling note saved' });
      setNote('');
    },
  });
  return (
    <form
      className={styles.form}
      onSubmit={(e) => {
        e.preventDefault();
        if (note.trim().length >= 3) save.mutate();
      }}
    >
      <TextArea label="Counselling note (optional)" value={note} onChange={setNote} />
      {save.isError ? (
        <Notice tone="error">{errorText(save.error, 'The note was not saved.')}</Notice>
      ) : null}
      <div>
        <Button type="submit" isPending={save.isPending} pendingLabel="Saving">
          Save note
        </Button>
      </div>
    </form>
  );
}

function CloseBalanceDialog({ line, onClose }: { line: WorkLineView; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [reason, setReason] = useState<(typeof CLOSE_REASONS)[number]['id']>('bought_elsewhere');
  const [note, setNote] = useState('');
  const close = useMutation({
    mutationFn: () =>
      closeBalance(line.id, {
        prescriptionLineVersion: line.version,
        reasonCode: reason,
        ...(note.trim() === '' ? {} : { note: note.trim() }),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: dispensingKeys.all });
      toasts.show({ title: 'Balance closed' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Close balance"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p>{`${formatQuantity(line.remaining, line.baseUnitDisplay)} of ${line.medicationDisplay} will not be given here.`}</p>
      <Select
        label="Reason"
        options={CLOSE_REASONS.map((r) => ({ id: r.id, label: r.label }))}
        value={reason}
        onChange={(v) => {
          setReason(v as typeof reason);
        }}
      />
      <TextArea
        label={reason === 'other' ? 'Why' : 'Note (optional)'}
        value={note}
        onChange={setNote}
      />
      {close.isError ? (
        <Notice tone="error">{errorText(close.error, 'The balance was not closed.')}</Notice>
      ) : null}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={() => {
            if (reason !== 'other' || note.trim() !== '') close.mutate();
          }}
          isPending={close.isPending}
          pendingLabel="Closing"
        >
          Close balance
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SubstituteDialog({
  storeId,
  onClose,
  onChoose,
}: {
  storeId: string;
  onClose: () => void;
  onChoose: (item: PharmacyItemView, reason: string) => void;
}) {
  const [item, setItem] = useState<PharmacyItemView | null>(null);
  const [reason, setReason] = useState('');
  return (
    <Dialog
      title="Substitute"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.meta}>
        Give only the same ingredient, strength and form. Say what you gave and why.
      </p>
      {item === null ? (
        <PharmacyItemPicker storeId={storeId} label="Give instead" onSelect={setItem} autoFocus />
      ) : (
        <p className={styles.strong}>{item.name}</p>
      )}
      <TextArea label="Reason" value={reason} onChange={setReason} />
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onPress={() => {
            if (item !== null && reason.trim().length >= 3) onChoose(item, reason.trim());
          }}
        >
          Substitute
        </Button>
      </DialogActions>
    </Dialog>
  );
}
